import {
  Avatar,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  TextField,
  Grid,
  Box,
  Button,
  FormHelperText,
  useTheme,
} from "@mui/material";

import { ChangeEvent, KeyboardEvent, useContext, useState } from "react";
import { Client } from "typesense";
import { SearchResponse } from "typesense/lib/Typesense/Documents";
import Iconify from "~src/components/Iconify";
import { BrandComparisonContext } from "~src/contexts/BrandComparisonContext";

const SEARCH_KEY = import.meta.env.VITE_SEARCH_KEY || "";
const SEARCH_NODE = import.meta.env.VITE_SEARCH_NODE || "";

const client = new Client({
  nodes: [
    {
      host: SEARCH_NODE,
      port: 443,
      protocol: "https",
    },
  ],
  apiKey: SEARCH_KEY,
  cacheSearchResultsForSeconds: 2 * 60,
});

interface SearchBarProps {
  handleAnalyseButtonClick: () => void;
}

export type BrandResult = {
  id: string;
  brand_name: string;
  description?: string;
  image?: string;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  handleAnalyseButtonClick,
}) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const { selectedBrands, setSelectedBrands } =
    useContext(BrandComparisonContext) || {};

  const [autocompleteResults, setAutocompleteResults] =
    useState<SearchResponse<BrandResult>>();

  const handleSearch = () => {
    if (searchText.length >= 4) {
      setShowClearButton(false);
    }
  };

  const handleClear = () => {
    setSearchText("");
    setShowClearButton(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (value.length < 2) {
      setShowClearButton(false);
      setAutocompleteResults(undefined);
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      autocomplete(value);
    }
  };

  const autocomplete = async (searchText: string) => {
    const results = await client
      .collections<BrandResult>("brands")
      .documents()
      .search({
        q: searchText,
        query_by: ["brand_name", "brand_url"],
      });
    setAutocompleteResults(results);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleBrandSelect = (brand: BrandResult) => {
    setSearchText("");
    setAnchorEl(null);
    if (setSelectedBrands) {
      setSelectedBrands((previousBrands) => {
        const brandExists = previousBrands.some((b) => b.id === brand.id);
        return brandExists ? previousBrands : [...previousBrands, brand];
      });
    }
  };


  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        borderRadius: 1,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: 3,
        mb: 5,
      }}
    >
      <Grid container spacing={2} sx={{
        alignItems: "center"
      }}>
        <Grid size={{ xs: 12 , sm: 10 }} >
          <Box
            sx={{
              display: "flex",
              mb: 1
            }}>
            <FormHelperText>Competitors</FormHelperText>
          </Box>
          <TextField
            disabled={selectedBrands && selectedBrands.length >= 5}
            placeholder="Search"
            fullWidth
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                style: { height: 45 },
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="iconamoon:search" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {showClearButton && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClear}>
                          <Iconify icon="uil:times-circle" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                ),
              }
            }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <List>
              {autocompleteResults?.hits?.map((result) => (
                <ListItem
                  sx={{
                    cursor: "pointer",
                    color: "inherit",
                    textDecoration: "none",
                  }}
                  onClick={() => handleBrandSelect(result.document)}
                  key={result.document.id}
                >
                  <ListItemAvatar>
                    <Avatar src={result.document.image} />
                  </ListItemAvatar>
                  <ListItemText primary={result.document.brand_name} />
                </ListItem>
              ))}
            </List>
          </Popover>
        </Grid>
        <Grid size={{ xs: 12 , sm: 2 }} >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.dark,
              borderRadius: "10px",
              height: "40px",
              "&:hover": {
                backgroundColor: "black",
              },
              mt: "29px",
            }}
            onClick={handleAnalyseButtonClick}
          >
            Analyse
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
